import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Check,
  AlertCircle,
  FileText,
  Lock,
  Paperclip,
  Send,
  Unlock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import FormModalLayout from '../../components/Common/FormModalLayout';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import { getAppHomePath } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { identityService, institutionService, licenseService, processService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { isRequesterRole } from '../../utils/tenantProfile';
import {
  checklistProgress,
  formatReqDate,
  requerimentoEventoLabel,
  requerimentoStatusMeta,
} from '../../utils/requerimentoStatus';
import { fetchAllPages } from '../../utils/apiPagination';
import { checklistTipoLabel, normalizeChecklistTipo } from '../../utils/checklistTipo';
import ChecklistThread from './ChecklistThread';

const STEPS = [
  { id: 1, title: 'Empreendimento', hint: 'Qual empreendimento será licenciado' },
  { id: 2, title: 'Tipo de licença', hint: 'O pedido que você quer protocolar' },
  { id: 3, title: 'Documentos', hint: 'Checklist e evidências' },
  { id: 4, title: 'Revisão', hint: 'Conferir e enviar ao órgão' },
];

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

function stripAnexo(row, anexoId) {
  if (!row?.checklist) return row;
  const checklist = row.checklist.map((item) => {
    const anexos = (item.anexos || []).filter((anexo) => String(anexo.id) !== String(anexoId));
    return { ...item, anexos, atendido: anexos.length > 0 };
  });
  const missing = checklist.some((item) => item.obrigatorio && !item.atendido);
  return {
    ...row,
    checklist,
    pronto_para_envio: Boolean(row.empreendimento_id && row.tipo_licenca_id && !missing),
  };
}

function Stepper({ step, onSelect, canJump }) {
  return (
    <nav className="mb-8" aria-label="Etapas do requerimento">
      <ol className="grid gap-3 sm:grid-cols-4">
        {STEPS.map((item) => {
          const active = step === item.id;
          const done = step > item.id;
          const jumpable = canJump && item.id !== step;
          return (
            <li key={item.id}>
              <button
                type="button"
                disabled={!jumpable}
                onClick={() => onSelect(item.id)}
                className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                  active
                    ? 'border-primary-500 bg-primary-50/80 shadow-sm ring-1 ring-primary-200/70'
                    : done
                      ? 'border-emerald-200 bg-emerald-50/60'
                      : 'border-dashed border-gray-200 bg-gray-50/60'
                } ${jumpable ? 'hover:border-primary-300' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                      active
                        ? 'bg-primary-600 text-white'
                        : done
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {done ? <Check className="h-4 w-4" /> : item.id}
                  </span>
                  <div>
                    <p className={`text-sm font-semibold ${active ? 'text-primary-950' : 'text-slate-800'}`}>{item.title}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{item.hint}</p>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function RequerimentoWizard() {
  const { id } = useParams();
  const isNew = !id || id === 'novo';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const requester = isRequesterRole(user);
  const [step, setStep] = useState(1);
  const stepped = useRef(false);
  const [empreendimentoId, setEmpreendimentoId] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [parecer, setParecer] = useState('');
  const [unlockAsk, setUnlockAsk] = useState('');
  const [openProc, setOpenProc] = useState(false);
  const [procForm, setProcForm] = useState({
    orgao_origem_id: '',
    setor_origem_id: '',
    numero: '',
    descricao: '',
  });

  const reqQuery = useQuery(['requerimento', id], () => processService.getRequerimento(id), {
    enabled: !isNew,
  });
  const row = reqQuery.data;

  const { data: enterprises = [] } = useQuery(
    ['req-enterprises'],
    () => fetchAllPages((params) => identityService.getEnterprises(params)),
    { refetchOnMount: 'always' }
  );
  const { data: tipos = [] } = useQuery(['req-tipos'], () =>
    fetchAllPages((params) => licenseService.getTiposLicenca(params))
  );
  const { data: previewChecklist = [] } = useQuery(
    ['req-tipo-checklist', tipoId],
    () => licenseService.listTipoChecklist(tipoId).then((r) => unwrap(r.data ?? r)),
    { enabled: Boolean(tipoId) && (isNew || !row?.checklist?.length) }
  );
  const { data: orgaos = [] } = useQuery(['req-orgaos'], () => institutionService.orgaoCrud.getList({ limit: 200 }), {
    enabled: !requester,
  });
  const { data: setores = [] } = useQuery(['req-setores'], () => institutionService.setorCrud.getList({ limit: 200 }), {
    enabled: !requester,
  });

  useEffect(() => {
    stepped.current = false;
  }, [id]);

  useEffect(() => {
    if (!row) return;
    setEmpreendimentoId(row.empreendimento_id || '');
    setTipoId(row.tipo_licenca_id || '');
    setObservacoes(row.observacoes || '');
    setProcForm((f) => ({
      ...f,
      descricao: row.observacoes || f.descricao,
    }));
    if (stepped.current || !row.empreendimento_id || !row.tipo_licenca_id) return;
    const reviewing = ['enviado', 'desbloqueio_solicitado'].includes(row.status);
    setStep(row.editavel || reviewing ? 3 : 4);
    stepped.current = true;
  }, [row]);

  const empName = useMemo(
    () => enterprises.find((e) => String(e.id) === String(empreendimentoId))?.name || 'Empreendimento',
    [enterprises, empreendimentoId]
  );
  const tipoName = useMemo(
    () => tipos.find((t) => String(t.id) === String(tipoId))?.nome || 'Tipo de licença',
    [tipos, tipoId]
  );

  const createMut = useMutation((payload) => processService.createRequerimento(payload), {
    onSuccess: (created) => {
      toast.success('Rascunho salvo');
      navigate(`/requerimentos/${created.id}`, { replace: true });
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const patchMut = useMutation((payload) => processService.updateRequerimento(row.id, payload), {
    onSuccess: () => queryClient.invalidateQueries(['requerimento', id]),
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const sendMut = useMutation(() => processService.enviarRequerimento(row.id), {
    onSuccess: () => {
      toast.success('Requerimento enviado ao órgão');
      queryClient.invalidateQueries(['requerimento', id]);
      queryClient.invalidateQueries(['requerimentos']);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const returnMut = useMutation(() => processService.devolverRequerimento(row.id, parecer), {
    onSuccess: () => {
      toast.success('Requerimento devolvido para ajuste');
      setParecer('');
      queryClient.invalidateQueries(['requerimento', id]);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const denyReqMut = useMutation(() => processService.indeferirRequerimento(row.id, parecer), {
    onSuccess: () => {
      toast.success('Requerimento indeferido');
      setParecer('');
      queryClient.invalidateQueries(['requerimento', id]);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const unlockAskMut = useMutation(() => processService.solicitarDesbloqueioRequerimento(row.id, unlockAsk), {
    onSuccess: () => {
      toast.success('Pedido de desbloqueio enviado');
      setUnlockAsk('');
      queryClient.invalidateQueries(['requerimento', id]);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const unlockMut = useMutation(() => processService.liberarEdicaoRequerimento(row.id), {
    onSuccess: () => {
      toast.success('Edição liberada');
      queryClient.invalidateQueries(['requerimento', id]);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const denyMut = useMutation(() => processService.negarDesbloqueioRequerimento(row.id, parecer || 'Pedido recusado.'), {
    onSuccess: () => {
      toast.success('Desbloqueio recusado');
      queryClient.invalidateQueries(['requerimento', id]);
    },
    onError: (e) => toast.error(userFacingError(e, 'saveError')),
  });
  const openMut = useMutation(
    () =>
      processService.abrirProcessoRequerimento(row.id, {
        ...procForm,
        usuario_id: user?.id || user?.sub || undefined,
      }),
    {
      onSuccess: (out) => {
        toast.success('Processo aberto');
        if (out?.avisos?.length) toast.error(out.avisos.join(' '));
        setOpenProc(false);
        queryClient.invalidateQueries(['requerimento', id]);
        if (out?.processo?.id) navigate(`/process/cases/${out.processo.id}`);
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const persistDraft = async () => {
    if (isNew) {
      if (!empreendimentoId || !tipoId) {
        toast.error('Selecione o empreendimento e o tipo de licença.');
        return null;
      }
      return createMut.mutateAsync({
        empreendimento_id: empreendimentoId,
        tipo_licenca_id: tipoId,
        observacoes,
      });
    }
    return patchMut.mutateAsync({
      empreendimento_id: empreendimentoId,
      tipo_licenca_id: tipoId,
      observacoes,
    });
  };

  const onUpload = async (itemId, file, input) => {
    if (!row?.id || !file) return;
    try {
      await processService.uploadRequerimentoAnexo(row.id, itemId, file);
      toast.success('Documento anexado');
      queryClient.invalidateQueries(['requerimento', id]);
    } catch (e) {
      toast.error(userFacingError(e, 'uploadError'));
    } finally {
      if (input) input.value = '';
    }
  };

  const onDeleteAnexo = async (anexoId) => {
    const key = ['requerimento', id];
    const previous = queryClient.getQueryData(key);
    queryClient.setQueryData(key, (current) => stripAnexo(current, anexoId));
    try {
      await processService.deleteRequerimentoAnexo(anexoId);
      toast.success('Documento removido');
      queryClient.invalidateQueries(key);
    } catch (e) {
      if (previous !== undefined) queryClient.setQueryData(key, previous);
      toast.error(userFacingError(e, 'saveError'));
    }
  };

  const openAnexo = async (anexo) => {
    try {
      const res = await processService.downloadRequerimentoAnexo(anexo.id);
      const blob = res?.data instanceof Blob ? res.data : res;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const meta = requerimentoStatusMeta(row?.status);
  const locked = Boolean(row && !row.editavel);
  const canAttach = requester && !locked && Boolean(row?.id);
  const checklist = row?.checklist?.length ? row.checklist : previewChecklist.map((item, idx) => ({
    id: item.id || `preview-${idx}`,
    descricao: item.descricao,
    tipo_checklist: normalizeChecklistTipo(item.tipo_checklist),
    obrigatorio: item.obrigatorio !== false,
    atendido: false,
    anexos: [],
  }));
  const progress = checklistProgress(row?.checklist || []);
  const missing = (row?.checklist || []).filter((item) => item.obrigatorio && !item.atendido);
  const canJump = Boolean(row?.id);
  const staffCanDecide = !requester && row && ['enviado', 'desbloqueio_solicitado'].includes(row.status);

  return (
    <FormPageShell>
      <PageHeader
        title={isNew ? 'Novo requerimento' : row?.numero || 'Requerimento'}
        description="Pedido de licenciamento em rascunho até o órgão abrir o processo."
        breadcrumbs={[
          { label: 'Início', to: getAppHomePath() },
          { label: 'Requerimentos', to: '/requerimentos' },
          { label: isNew ? 'Novo' : row?.numero || 'Detalhe' },
        ]}
      />

      {reqQuery.isError ? <QueryErrorBanner error={reqQuery.error} /> : null}

      {row ? (
        <div className={`mb-6 rounded-2xl border px-5 py-4 shadow-sm ${meta.chip}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${meta.className}`}>{meta.label}</span>
                {locked ? (
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
                    <Lock className="h-3.5 w-3.5" /> Bloqueado para edição
                  </span>
                ) : (
                  <span className="text-xs font-medium text-emerald-700">Rascunho editável</span>
                )}
              </div>
              <p className="mt-2 text-sm text-slate-600">{meta.hint}</p>
              {row.parecer_orgao && ['devolvido', 'desbloqueio_solicitado', 'indeferido'].includes(row.status) ? (
                <p className="mt-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong>Pendência do órgão:</strong> {row.parecer_orgao}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-4">
              <div className="w-40">
                <div className="mb-1 flex justify-between text-[11px] text-slate-500">
                  <span>Checklist</span>
                  <span>
                    {progress.done}/{progress.total || 0}
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-white/80">
                  <div
                    className={`h-full rounded-full ${progress.pct === 100 ? 'bg-emerald-500' : 'bg-primary-500'}`}
                    style={{ width: `${progress.pct}%` }}
                  />
                </div>
              </div>
              {row.processo_id ? (
                <Link to={`/process/cases/${row.processo_id}`} className="btn btn-outline">
                  Ver processo
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <Stepper step={step} onSelect={setStep} canJump={canJump} />

      {step === 1 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Empreendimento</h2>
          <p className="mt-1 text-sm text-slate-500">O pedido fica vinculado a um empreendimento seu. O cadastro de processo só acontece depois da análise.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {enterprises.map((ent) => {
              const selected = String(ent.id) === String(empreendimentoId);
              return (
                <button
                  key={ent.id}
                  type="button"
                  disabled={locked}
                  onClick={() => setEmpreendimentoId(ent.id)}
                  className={`rounded-xl border px-4 py-3 text-left transition ${
                    selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200' : 'border-slate-200 hover:border-primary-300'
                  }`}
                >
                  <p className="font-medium text-slate-900">{ent.name}</p>
                  <p className="text-xs text-slate-500">{ent.city ? `${ent.city}/${ent.state || ''}` : 'Sem cidade'}</p>
                </button>
              );
            })}
          </div>
          {enterprises.length === 0 ? (
            <div className="mt-4 rounded-xl border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
              Cadastre um empreendimento antes de requerer.{' '}
              <Link to="/enterprises" className="font-medium text-primary-700 hover:underline">
                Ir para empreendimentos
              </Link>
            </div>
          ) : null}
          <div className="mt-6 flex justify-end">
            <button type="button" className="btn btn-primary" disabled={!empreendimentoId} onClick={() => setStep(2)}>
              Continuar
            </button>
          </div>
        </section>
      ) : null}

      {step === 2 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Tipo de licença</h2>
          <p className="mt-1 text-sm text-slate-500">O checklist obrigatório vem do tipo escolhido e fica salvo no rascunho.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {tipos.map((tipo) => {
              const selected = String(tipo.id) === String(tipoId);
              return (
                <button
                  key={tipo.id}
                  type="button"
                  disabled={locked}
                  onClick={() => setTipoId(tipo.id)}
                  className={`rounded-xl border px-4 py-3 text-left ${
                    selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-200' : 'border-slate-200 hover:border-primary-300'
                  }`}
                >
                  <p className="font-medium text-slate-900">{tipo.nome}</p>
                  {tipo.sigla ? <p className="text-xs text-slate-500">{tipo.sigla}</p> : null}
                </button>
              );
            })}
          </div>
          {tipoId && previewChecklist.length ? (
            <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Checklist deste tipo</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {previewChecklist.map((item) => (
                  <li key={item.id}>
                    {item.descricao}
                    {item.obrigatorio !== false ? (
                      <span className="ml-2 text-xs font-semibold text-rose-600">Obrigatório</span>
                    ) : (
                      <span className="ml-2 text-xs text-slate-400">Opcional</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="mt-6 flex justify-between">
            <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!tipoId || createMut.isLoading}
              onClick={async () => {
                await persistDraft();
                setStep(3);
              }}
            >
              {isNew ? 'Salvar rascunho e continuar' : 'Continuar'}
            </button>
          </div>
        </section>
      ) : null}

      {step === 3 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Documentos do checklist</h2>
          <p className="mt-1 text-sm text-slate-500">
            {empName} · {tipoName}.{' '}
            {requester
              ? 'Anexe ao menos um arquivo em cada item obrigatório. Se o órgão devolver o pedido, responda em cada item apontado.'
              : 'Os documentos ficam somente para consulta. Aponte em cada item o que precisa de ajuste.'}
          </p>
          <div className="mt-5 space-y-3">
            {checklist.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
                Este tipo ainda não tem checklist. Você pode enviar o requerimento após revisar.
              </p>
            ) : (
              checklist.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 px-4 py-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">
                        <span className="mr-2 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                          {checklistTipoLabel(normalizeChecklistTipo(item.tipo_checklist))}
                        </span>
                        {item.descricao}
                        {item.obrigatorio ? (
                          <span className="ml-2 text-xs font-semibold text-rose-600">Obrigatório</span>
                        ) : (
                          <span className="ml-2 text-xs text-slate-400">Opcional</span>
                        )}
                      </p>
                      <p className={`mt-1 text-xs ${item.atendido ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {item.atendido ? 'Documento anexado' : 'Aguardando documento'}
                      </p>
                    </div>
                    {canAttach ? (
                      <label className="btn btn-outline cursor-pointer">
                        <Paperclip className="mr-2 h-4 w-4" />
                        Anexar
                        <input type="file" className="hidden" onChange={(e) => onUpload(item.id, e.target.files?.[0], e.target)} />
                      </label>
                    ) : locked || !row?.id ? (
                      <Lock className="h-4 w-4 text-slate-400" />
                    ) : null}
                  </div>
                  {item.anexos?.length ? (
                    <ul className="mt-3 space-y-1">
                      {item.anexos.map((anexo) => (
                        <li key={anexo.id} className="flex items-center justify-between text-sm text-slate-700">
                          <button type="button" className="text-left text-primary-700 hover:underline" onClick={() => openAnexo(anexo)}>
                            {anexo.filename_original}
                          </button>
                          {canAttach ? (
                            <button type="button" className="text-rose-600" onClick={() => onDeleteAnexo(anexo.id)}>
                              Remover
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {row?.id && !String(item.id).startsWith('preview-') ? (
                    <ChecklistThread
                      reqId={row.id}
                      item={item}
                      status={row.status}
                      requester={requester}
                      userId={user?.id || user?.sub || ''}
                    />
                  ) : null}
                </div>
              ))
            )}
          </div>
          <label className="mt-5 block">
            <span className="form-label">Observações</span>
            <textarea
              className="form-input min-h-[96px]"
              disabled={locked}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              onBlur={() => row?.editavel && persistDraft()}
              placeholder="Informações extras para o analista"
            />
          </label>
          <div className="mt-6 flex justify-between">
            <button type="button" className="btn btn-outline" onClick={() => setStep(2)}>
              Voltar
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(4)}>
              Revisar
            </button>
          </div>
        </section>
      ) : null}

      {step === 4 ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900">Revisão</h2>
          <dl className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <dt className="text-xs uppercase text-slate-500">Empreendimento</dt>
              <dd className="font-medium text-slate-900">{empName}</dd>
            </div>
            <div className="rounded-lg bg-slate-50 px-4 py-3">
              <dt className="text-xs uppercase text-slate-500">Tipo</dt>
              <dd className="font-medium text-slate-900">{tipoName}</dd>
            </div>
          </dl>
          {missing.length ? (
            <p className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
              Faltam documentos em {missing.length} item(ns) obrigatório(s). Conclua o checklist para habilitar o envio.
            </p>
          ) : (
            <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              Checklist obrigatório atendido. Você pode requerer o processo.
            </p>
          )}

          {requester && row?.editavel ? (
            <div className="mt-6 flex flex-wrap justify-between gap-3">
              <button type="button" className="btn btn-outline" onClick={() => setStep(3)}>
                Voltar aos documentos
              </button>
              <button
                type="button"
                className="btn btn-primary inline-flex items-center gap-2"
                disabled={!row?.pronto_para_envio || sendMut.isLoading}
                onClick={() => sendMut.mutate()}
              >
                <Send className="h-4 w-4" />
                Requerer processo
              </button>
            </div>
          ) : null}

          {requester && row?.status === 'enviado' ? (
            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-800">Precisa alterar algo?</p>
              <p className="mt-1 text-sm text-slate-500">
                Depois do envio o rascunho fica bloqueado. O órgão precisa autorizar a reabertura.
              </p>
              <textarea
                className="form-input mt-3 min-h-[80px]"
                value={unlockAsk}
                onChange={(e) => setUnlockAsk(e.target.value)}
                placeholder="Descreva o que deseja ajustar"
              />
              <button
                type="button"
                className="btn btn-outline mt-3 inline-flex items-center gap-2"
                disabled={unlockAsk.trim().length < 3 || unlockAskMut.isLoading}
                onClick={() => unlockAskMut.mutate()}
              >
                <Unlock className="h-4 w-4" />
                Solicitar desbloqueio
              </button>
            </div>
          ) : null}

          {requester && row?.status === 'desbloqueio_solicitado' ? (
            <p className="mt-6 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-950">
              Pedido de desbloqueio enviado. Aguarde o órgão liberar a edição.
            </p>
          ) : null}

          {staffCanDecide ? (
            <div className="mt-6 space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div>
                <h3 className="font-semibold text-slate-900">Análise do órgão</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Aponte em cada item do checklist o que precisa mudar. Ao devolver, essas mensagens ficam registradas e o requerente responde em cada item. O texto abaixo vale para o pedido inteiro.
                </p>
              </div>
              {row.status === 'desbloqueio_solicitado' ? (
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn btn-primary" onClick={() => unlockMut.mutate()}>
                    Liberar edição
                  </button>
                  <button type="button" className="btn btn-outline" onClick={() => denyMut.mutate()}>
                    Manter bloqueado
                  </button>
                </div>
              ) : null}
              <textarea
                className="form-input min-h-[90px]"
                value={parecer}
                onChange={(e) => setParecer(e.target.value)}
                placeholder="Descreva o que falta ou está incorreto para devolver ou indeferir"
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={parecer.trim().length < 3 || returnMut.isLoading}
                  onClick={() => returnMut.mutate()}
                >
                  Devolver para ajuste
                </button>
                <button
                  type="button"
                  className="btn btn-outline text-rose-700"
                  disabled={parecer.trim().length < 3 || denyReqMut.isLoading}
                  onClick={() => denyReqMut.mutate()}
                >
                  Indeferir
                </button>
                <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={() => setOpenProc(true)}>
                  <FileText className="h-4 w-4" />
                  Abrir processo
                </button>
              </div>
            </div>
          ) : null}

          {row?.eventos?.length ? (
            <ol className="mt-6 space-y-2 border-t border-slate-100 pt-4">
              {row.eventos.map((ev) => (
                <li key={ev.id} className="flex flex-wrap items-baseline gap-2 text-sm text-slate-600">
                  <span className="font-medium text-slate-800">{requerimentoEventoLabel(ev.tipo)}</span>
                  {ev.mensagem ? <span>— {ev.mensagem}</span> : null}
                  <span className="text-xs text-slate-400">{formatReqDate(ev.created_at)}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </section>
      ) : null}

      {openProc ? (
        <FormModalLayout
          title="Abrir processo"
          subtitle="Os dados do requerimento serão copiados. Complete o que o cadastro de processo exige."
          onClose={() => setOpenProc(false)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setOpenProc(false)}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!procForm.orgao_origem_id || !procForm.setor_origem_id || openMut.isLoading}
                onClick={() => openMut.mutate()}
              >
                Confirmar abertura
              </button>
            </>
          }
        >
          <div className="form-stack">
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Número do requerimento: <strong>{row?.numero}</strong>. Informe o número oficial do processo, se já existir.
            </p>
            <label>
              <span className="form-label">Número do processo</span>
              <input
                className="form-input"
                value={procForm.numero}
                onChange={(e) => setProcForm((f) => ({ ...f, numero: e.target.value }))}
                placeholder={row?.numero || 'Número do processo'}
              />
            </label>
            <label>
              <span className="form-label">Órgão de origem</span>
              <select
                className="form-input"
                value={procForm.orgao_origem_id}
                onChange={(e) => setProcForm((f) => ({ ...f, orgao_origem_id: e.target.value }))}
              >
                <option value="">Selecione</option>
                {unwrap(orgaos).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nome || o.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="form-label">Setor de origem</span>
              <select
                className="form-input"
                value={procForm.setor_origem_id}
                onChange={(e) => setProcForm((f) => ({ ...f, setor_origem_id: e.target.value }))}
              >
                <option value="">Selecione</option>
                {unwrap(setores).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome || s.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span className="form-label">Descrição</span>
              <textarea
                className="form-input min-h-[80px]"
                value={procForm.descricao}
                onChange={(e) => setProcForm((f) => ({ ...f, descricao: e.target.value }))}
              />
            </label>
          </div>
        </FormModalLayout>
      ) : null}
    </FormPageShell>
  );
}
